import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { TimeBlockItemBuilderService } from '../../../../features/calendar/components/time-block/generation/time-block-item-builder.service';
import { ITimeBlockComponentItem } from '../../../../features/calendar/components/time-block/types/time-block-component-item-types';
import { TimeBlockContentType } from '../../../../features/calendar/components/time-block/types/time-block-types';
import { TimeBlockModifiedResponse } from '../../../../shared/data-types/http-response-types';
import { UserOperation } from '../../../enums/user-operation';
import * as fromTimeBlockActions from './time-blocks.actions';
import { timeBlocksKey } from './time-blocks.selectors';

/** Service used for calendar and absence planner **/
@Injectable()
export class TimeBlockHttpService {
  constructor(
    private readonly store: Store,
    private readonly timeBlockItemBuilderService: TimeBlockItemBuilderService,
    private readonly actions$: Actions,
  ) {}

  public fetchTimeBlocks(
    startDate: Date,
    endDate: Date,
    selectedUserIds: number[],
    isBillable: boolean | null,
    isInvoiced: boolean | null,
    timeBlockTypes: TimeBlockContentType[],
  ): Observable<readonly ITimeBlockComponentItem[]> {
    this.store.dispatch(
      fromTimeBlockActions.fetchTimeBlockList({
        payload: {
          selectedUserIds,
          startDate,
          endDate,
          isBillable,
          isInvoiced,
          timeBlockTypes,
        },
      }),
    );

    return this.actions$.pipe(
      ofType(fromTimeBlockActions.setInitialTimeBlocks),
      take(1),
      map((response) => response.payload),
    );
  }

  fetchTimeBlocksForTasks(
    taskIds: number[],
    isInvoiced: boolean | null,
    includeActiveTimeBlock: boolean,
  ): Observable<readonly ITimeBlockComponentItem[]> {
    this.store.dispatch(
      fromTimeBlockActions.fetchTimeBlocksForTasks({
        payload: {
          taskIds,
          isInvoiced,
          includeActiveTimeBlock,
        },
      }),
    );

    return this.actions$.pipe(
      ofType(fromTimeBlockActions.setTimeBlocksForTask),
      map((result) => result.payload),
    );
  }

  public fetchActiveTimerTimeBlock(userId: number): Observable<ITimeBlockComponentItem> {
    this.store.dispatch(fromTimeBlockActions.fetchActiveTimerTimeBlock({ payload: userId }));

    return this.actions$.pipe(
      ofType(fromTimeBlockActions.setActiveTimerTimeBlock),
      map((result) => result.payload),
      take(1),
    );
  }

  public postTimeBlock(
    toBeAddedTimeBlock: ITimeBlockComponentItem,
  ): Observable<TimeBlockModifiedResponse> {
    const clonedTimeBlock = this.timeBlockItemBuilderService.buildFromExisting(
      toBeAddedTimeBlock,
      toBeAddedTimeBlock.timeBlockContentType,
    );
    // Remove this property, since it is not allowed to mutate ngrx state.
    // Angular mutates componentRef through change detection while dispatching.
    delete clonedTimeBlock.timeBlockModel.componentRef;

    this.store.dispatch(
      fromTimeBlockActions.addTimeBlock({
        payload: clonedTimeBlock,
      }),
    );

    return this.actions$.pipe(
      ofType(fromTimeBlockActions.timeBlockAdded),
      map((result) => result.payload),
    );
  }

  public putTimeBlock(
    toBeUpdatedTimeBlock: ITimeBlockComponentItem,
    storeInHistory = true,
  ): Observable<TimeBlockModifiedResponse> {
    const clonedTimeBlock = this.timeBlockItemBuilderService.buildFromExisting(
      toBeUpdatedTimeBlock,
      toBeUpdatedTimeBlock.timeBlockContentType,
    );
    // Remove componentRef property, since it is not allowed to mutate ngrx state.
    // Angular mutates componentRef through change detection while dispatching.
    delete clonedTimeBlock.timeBlockModel.componentRef;

    this.store.dispatch(
      fromTimeBlockActions.updateTimeBlock({
        payload: clonedTimeBlock,
        storeInHistory,
        undoRedoMeta: {
          userOperation: UserOperation.Update,
          undoableFn: this.putTimeBlock.bind(this),
          undoableId: toBeUpdatedTimeBlock.id,
          undoableType: timeBlocksKey,
        },
      }),
    );

    return this.actions$.pipe(
      ofType(fromTimeBlockActions.timeBlockUpdated),
      map((result) => result.payload),
    );
  }

  public deleteBlock(timeBlockId: number): Observable<unknown> {
    this.store.dispatch(
      fromTimeBlockActions.deleteTimeBlock({
        payload: timeBlockId,
      }),
    );

    return this.actions$.pipe(ofType(fromTimeBlockActions.timeBlockDeleted));
  }

  public deleteMultipleBlocks(timeBlockIds: number[]): Observable<unknown> {
    this.store.dispatch(
      fromTimeBlockActions.deleteMultipleTimeBlocks({
        payload: timeBlockIds,
      }),
    );

    return this.actions$.pipe(ofType(fromTimeBlockActions.multipleTimeBlocksDeleted));
  }
}
